'use strict';

var React = require('react');
var functions = require('../../helpers/functions.js');
var index = require('../Flex/index.js');
var component = require('../Text/component.js');
require('./Cells/cell.js');
require('./Cells/cellActions.js');
require('./Cells/cellCheckbox.styles.js');
require('../Chip/component.js');
require('../Tooltip/component.js');
require('./Cells/cellIcon.styles.js');
require('../Pill/pill.styles.js');
require('../Pill/pillButton.styles.js');
require('../forms/Select/Control.js');
require('../forms/Select/Option.js');
require('../forms/Select/multi-select/OptionMulti.js');
require('../forms/Select/multi-select/ControlMulti.js');
require('./Cells/cellSubText.styles.js');
require('../forms/Switch/component.js');
require('../Tag/component.js');
var header = require('./Cells/header.js');
require('../Icon/component.js');
var paginator = require('./paginator.js');
var row = require('./row.js');
var table_styles = require('./table.styles.js');
var tableData = require('./tableData.js');
var useExpandableRows = require('./useExpandableRows.js');
var useHidableCols = require('./useHidableCols.js');
var useResizableCols = require('./useResizableCols.js');
var useSelectableRows = require('./useSelectableRows.js');

function _interopDefaultCompat (e) { return e && typeof e === 'object' && 'default' in e ? e : { default: e }; }

var React__default = /*#__PURE__*/_interopDefaultCompat(React);

function IziTable({ columns, initialOnPageChangeCallbackDisabled = false, engine, renderExpandedRow, actionMenuLeftSlot, actionMenuRightSlot, getRowOptions, id, itemsPerPage = 20, labelEmptySub, labelEmptyTitle, labelSettingsCancel, labelSettingsSave, labelSettingsTitle, onRowFocus, onRowClick, onPageChange = functions.noop, onQueryParamsChange, onParamsChange, tableLayout = 'fixed', total, variant = 'body-light', withHidableCols = false, hidableColsLocalStorageKey, withSelectableRows = false, ...props }) {
    const [scrollPosition, setScrollPosition] = React.useState(0);
    const { columnsToShow, hidableColsSettingsElement } = useHidableCols.useHidableCols(id, columns, withHidableCols, hidableColsLocalStorageKey, labelSettingsTitle, labelSettingsSave, labelSettingsCancel);
    const { columnsWithExpandable } = useExpandableRows.useExpandableRows(engine, renderExpandedRow !== undefined, getRowOptions);
    const { columnsWithSelectable } = useSelectableRows.useSelectableRows(engine, withSelectableRows);
    const columnsForTable = React.useMemo(() => [...columnsWithSelectable, ...columnsWithExpandable, ...columnsToShow], [columnsWithSelectable, columnsWithExpandable, columnsToShow]);
    const { colSize, makeOnResizeMouseDown, resizing } = useResizableCols.useResizableCols(id, columnsForTable);
    const makeHandleFocusForRow = React.useCallback((d) => () => {
        if (d !== engine.state.focusRow && onRowFocus) {
            engine.setFocusRow(d);
            onRowFocus(d);
        }
    }, [engine, onRowFocus]);
    const handlePageChange = React.useCallback((page, _itemsPerPage) => {
        engine.setPage(page);
    }, [engine]);
    // Handle depreacted props
    // Use state for `initialOnPageChangeCallbackDisabled` so it's initial value is constant
    const [_initialOnPageChangeCallbackDisabled] = React.useState(initialOnPageChangeCallbackDisabled);
    // Make refs to avoid re-rendering when these props change
    const itemsPerPageRef = React.useRef(itemsPerPage);
    const onPageChangeRef = React.useRef(onPageChange);
    const onQueryParamsChangeRef = React.useRef(onQueryParamsChange);
    const onParamsChangeRef = React.useRef(onParamsChange);
    // Update refs when props change
    React.useEffect(() => {
        onPageChangeRef.current = onPageChange;
        onQueryParamsChangeRef.current = onQueryParamsChange;
        onParamsChangeRef.current = onParamsChange;
        itemsPerPageRef.current = itemsPerPage;
    }, [onQueryParamsChange, itemsPerPage, onPageChange, onParamsChange]);
    const isInitialRenderRef = React.useRef(true);
    // Call `onPageChange` on initial render (if not disabled) and on page change
    React.useEffect(() => {
        if (isInitialRenderRef.current && _initialOnPageChangeCallbackDisabled)
            return;
        const currentOnPageChange = onPageChangeRef.current;
        currentOnPageChange(engine.state.page, itemsPerPageRef.current);
    }, [_initialOnPageChangeCallbackDisabled, engine.state.page]);
    // Call onQueryParamsChange when query params change but not on initial render
    React.useEffect(() => {
        if (isInitialRenderRef.current)
            return;
        const currentOnQueryParamsChange = onQueryParamsChangeRef.current;
        currentOnQueryParamsChange?.(engine.state.queryParams);
    }, [engine.state.queryParams]);
    React.useEffect(() => {
        isInitialRenderRef.current = false;
    }, []);
    // Call onParamsChange when page, itemsPerPage or queryParams change
    React.useEffect(() => {
        const currentOnParamsChange = onParamsChangeRef.current;
        currentOnParamsChange?.({
            page: engine.state.page,
            itemsPerPage,
            queryParams: engine.state.queryParams,
        });
    }, [engine.state.page, itemsPerPage, engine.state.queryParams]);
    const [openPopinKey, setOpenPopinKey] = React.useState(null);
    const _total = engine.state.total ?? total ?? 0;
    const wrapperId = `tableWrapper-${id}`;
    const handleScroll = () => {
        const position = window.pageYOffset;
        setScrollPosition(position);
    };
    React.useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);
    return (React__default.default.createElement(component.Text, { variant: variant, as: "div", id: wrapperId },
        (actionMenuLeftSlot || actionMenuRightSlot || hidableColsSettingsElement) && (React__default.default.createElement(table_styles.ActionMenuContainer, null,
            actionMenuLeftSlot ? actionMenuLeftSlot : null,
            actionMenuRightSlot || hidableColsSettingsElement ? (React__default.default.createElement(table_styles.RightActionMenuContainer, null,
                React__default.default.createElement(index.Flex.Row, { space: "s" },
                    actionMenuRightSlot,
                    hidableColsSettingsElement))) : null)),
        React__default.default.createElement(table_styles.TableStyled, { id: id, tableLayout: tableLayout, "aria-busy": engine.state.isLoading, ...props, onScroll: handleScroll },
            React__default.default.createElement("colgroup", null, columnsForTable.map(c => (React__default.default.createElement("col", { id: `${id}-col-${c.key}`, key: `${c.key}-col`, width: colSize[c.key] })))),
            React__default.default.createElement("thead", null,
                React__default.default.createElement(row.RowHeader, { "$grabColsVisible": openPopinKey !== null || resizing }, columnsForTable.map((c, i) => {
                    const headerProps = {
                        id: `${id}-header-${c.key}`,
                        tableId: wrapperId,
                        filter: c.filter,
                        defaultOrder: c.defaultOrder,
                        renderPopin: c.renderPopin,
                        colKey: c.key,
                        addToQueryParams: engine.addToQueryParams,
                        removeFromQueryParams: engine.removeFromQueryParams,
                        queryParam: engine.getQueryParamByKey(c.key),
                        resizeHidden: i + 1 === columnsForTable.length,
                        resizeOnMouseDown: makeOnResizeMouseDown(c.key),
                        isPopinOpen: openPopinKey === c.key,
                        setOpenPopinKey,
                        children: c.header,
                    };
                    const _renderHeader = c.renderHeader || c.headerCustom;
                    const header$1 = _renderHeader ? _renderHeader(headerProps) : React__default.default.createElement(header.Header, { ...headerProps });
                    return React__default.default.createElement(React.Fragment, { key: `${c.key}-header` }, header$1);
                }))),
            React__default.default.createElement("tbody", null,
                React__default.default.createElement(tableData.TableData, { engine: engine, total: _total, itemsPerPage: itemsPerPage, page: engine.state.page, columns: columnsForTable, id: id, onRowClick: onRowClick, makeHandleFocusForRow: makeHandleFocusForRow, labelEmptyTitle: labelEmptyTitle, labelEmptySub: labelEmptySub, getRowOptions: getRowOptions, renderExpandedRow: renderExpandedRow }))),
        React__default.default.createElement(paginator.Paginator, { itemsTotal: _total, itemsPerPage: itemsPerPage, page: engine.state.page, onPageChange: handlePageChange, scrollPosition: scrollPosition })));
}

exports.IziTable = IziTable;
//# sourceMappingURL=table.js.map
